import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import eBooks from "./EbookData";

const EbookDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      const foundBook = eBooks.find((b) => b.id === parseInt(id));
      setBook(foundBook || null);
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, [id]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center px-4">
        <div className="flex flex-col items-center">
          <div className="relative w-24 h-24 mb-6">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 animate-spin border-t-4 border-transparent"></div>
            <div className="absolute inset-4 bg-gray-900 rounded-full"></div>
          </div>
          <p className="text-gray-400 font-medium tracking-wider">
            LOADING EBOOK...
          </p>
        </div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center px-4">
        <div className="bg-gray-800/70 backdrop-blur-xl p-8 rounded-2xl shadow-2xl border border-gray-700/50 text-center max-w-md">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 bg-red-500/20 rounded-full animate-ping"></div>
            <div className="absolute inset-2 bg-red-500 rounded-full flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-10 w-10"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-red-400 mb-2 tracking-wide">
            Book Not Found
          </h2>
          <p className="text-gray-400 mb-6">No eBook found with ID: {id}</p>
          <button
            className="mt-4 px-8 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-xl text-white font-medium tracking-wide hover:from-blue-500 hover:to-cyan-400 transition-all duration-300 transform hover:-translate-y-1 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40"
            onClick={() => navigate("/")}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Safely get author initials
  const authorInitial = book.author ? book.author.charAt(0) : "?";
  const authorName = book.author || "Unknown Author";
  const bookRating = book.rating || 0;
  const bookReviews = book.reviews || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 py-12 px-4 text-white overflow-hidden">
      {/* Floating Particles Background */}
      <div className="absolute inset-0 overflow-hidden z-0">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-cyan-500/10 animate-float"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              width: `${Math.random() * 20 + 5}px`,
              height: `${Math.random() * 20 + 5}px`,
              animationDuration: `${Math.random() * 15 + 10}s`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      {/* Back Button */}
      <button
        onClick={() => navigate("/")}
        className="absolute top-6 left-6 z-10 p-3 bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 hover:bg-gray-700/50 transition-all duration-300 group"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 text-gray-300 group-hover:text-cyan-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
      </button>

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Book Cover */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-tr from-cyan-500/20 to-blue-600/20 rounded-2xl blur-xl opacity-50 animate-pulse"></div>
            <div className="relative overflow-hidden rounded-2xl border border-gray-800 shadow-2xl">
              <img
                src={book.image}
                alt={book.title}
                className="w-full h-auto object-cover transition-all duration-500 hover:scale-105"
              />
            </div>

            {/* Decorative Elements */}
            <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 blur-2xl opacity-30"></div>
            <div className="absolute -top-4 -left-4 w-16 h-16 rounded-full bg-cyan-500/30 blur-xl"></div>
          </div>

          {/* Book Details */}
          <div className="bg-gray-800/30 backdrop-blur-lg p-8 rounded-2xl border border-gray-700/50 shadow-xl">
            <div className="mb-6">
              <span className="inline-block px-3 py-1 bg-cyan-500/10 text-cyan-400 rounded-full text-sm font-medium mb-4">
                {book.category || "Ebook"}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                {book.title}
              </h1>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                {book.description || "No description available."}
              </p>
            </div>

            {/* Author & Rating */}
            <div className="flex flex-wrap items-center gap-6 mb-8">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center mr-3">
                  <span className="text-white font-bold">{authorInitial}</span>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Author</p>
                  <p className="font-medium">{authorName}</p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="mr-3">
                  <div className="flex items-center mb-1">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-5 h-5 ${
                          i < Math.round(bookRating)
                            ? "text-yellow-400"
                            : "text-gray-700"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-sm text-gray-400">
                    {bookRating}/5 from {bookReviews} reviews
                  </p>
                </div>
              </div>
            </div>

            {/* Pricing & Action */}
            <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-gray-700/50">
              <div>
                <p className="text-gray-400 text-sm">Price</p>
                <div className="flex items-baseline">
                  <span className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                    {book.price || "$0.00"}
                  </span>
                  {book.originalPrice && (
                    <span className="ml-2 text-gray-500 line-through">
                      {book.originalPrice}
                    </span>
                  )}
                </div>
              </div>

              <button
                onClick={() => navigate(`/payment/${book.id}`)}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-xl text-white font-bold tracking-wide hover:from-blue-500 hover:to-cyan-400 transition-all duration-300 transform hover:-translate-y-1 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                    clipRule="evenodd"
                  />
                </svg>
                Buy Now
              </button>
            </div>

            {/* Book Details */}
            <div className="grid grid-cols-2 gap-4 mt-8">
              <div className="bg-gray-800/50 p-4 rounded-xl">
                <p className="text-gray-400 text-sm">Format</p>
                <p className="font-medium">PDF, EPUB, MOBI</p>
              </div>
              <div className="bg-gray-800/50 p-4 rounded-xl">
                <p className="text-gray-400 text-sm">Pages</p>
                <p className="font-medium">{book.pages || "N/A"}</p>
              </div>
              <div className="bg-gray-800/50 p-4 rounded-xl">
                <p className="text-gray-400 text-sm">Language</p>
                <p className="font-medium">English</p>
              </div>
              <div className="bg-gray-800/50 p-4 rounded-xl">
                <p className="text-gray-400 text-sm">File Size</p>
                <p className="font-medium">15.2 MB</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Action Button */}
      <div className="fixed bottom-8 right-8 z-10">
        <button className="w-14 h-14 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 flex items-center justify-center shadow-xl shadow-blue-500/30 hover:shadow-blue-500/50 transition-all">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default EbookDetails;
